// SeriesPage — управление сериями (collections статей).
//
// Серия = группа связанных Editorial-статей, отрисовывается в Reverium
// reader как "Part 3 of 6 · Light & Lens" с навигацией. На admin'е —
// CRUD списка + при клике на серию открывается панель с её статьями
// (drag-drop reorder в будущем; сейчас вручную через series_position).
//
// Создание: title → slug автогенерируется на сервере (pb_hooks/extras.pb.js).
// Cover: optional file upload.

import { useEffect, useMemo, useState } from "react";
import {
  list,
  createRecord,
  updateRecord,
  deleteRecord,
  fileUrl,
} from "../lib/pb";
import { useT, type T } from "../lib/i18n";
import type { Article, Series } from "../lib/types";

export default function SeriesPage() {
  const t = useT();
  const [series, setSeries] = useState<Series[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        list<Series>("series", { perPage: 200, sort: "-updated" }),
        list<Article>("articles", { perPage: 500, sort: "series_position" }),
      ]);
      setSeries(s.items);
      setArticles(a.items);
    } finally {
      setLoading(false);
    }
  }

  const articlesBySeries = useMemo(() => {
    const map = new Map<string, Article[]>();
    for (const a of articles) {
      if (!a.series) continue;
      if (!map.has(a.series)) map.set(a.series, []);
      map.get(a.series)!.push(a);
    }
    // Sort each by series_position ascending
    for (const arr of map.values()) {
      arr.sort((x, y) => (x.series_position || 999) - (y.series_position || 999));
    }
    return map;
  }, [articles]);

  async function handleCreate() {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const r = await createRecord<Series>("series", {
        title: newTitle.trim(),
        description: "",
      });
      setNewTitle("");
      setSelectedId(r.id);
      await load();
    } catch (err: any) {
      alert("Failed to create series: " + (err?.message || err));
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(s: Series) {
    if (!confirm(`Delete series "${s.title}"? Articles will keep their content but lose the series link.`)) return;
    try {
      await deleteRecord("series", s.id);
      if (selectedId === s.id) setSelectedId(null);
      await load();
    } catch (err: any) {
      alert("Failed to delete: " + (err?.message || err));
    }
  }

  async function handleSaveMeta(s: Series, patch: Partial<Series>) {
    try {
      await updateRecord<Series>("series", s.id, patch);
      await load();
    } catch (err: any) {
      alert("Save failed: " + (err?.message || err));
    }
  }

  async function handleUploadCover(s: Series, file: File) {
    const fd = new FormData();
    fd.append("cover", file);
    try {
      await updateRecord<Series>("series", s.id, fd);
      await load();
    } catch (err: any) {
      alert("Cover upload failed: " + (err?.message || err));
    }
  }

  async function handleReorderArticle(a: Article, newPos: number) {
    if (newPos < 1) newPos = 1;
    try {
      await updateRecord<Article>("articles", a.id, { series_position: newPos });
      await load();
    } catch (err: any) {
      alert("Reorder failed: " + (err?.message || err));
    }
  }

  async function handleRemoveFromSeries(a: Article) {
    try {
      await updateRecord<Article>("articles", a.id, {
        series: "",
        series_position: 0,
      });
      await load();
    } catch (err: any) {
      alert("Remove failed: " + (err?.message || err));
    }
  }

  const selectedSeries = series.find((s) => s.id === selectedId) || null;

  if (loading) return <div className="empty">{t.loading}</div>;

  return (
    <div style={{ padding: "24px 32px", display: "flex", gap: 24, height: "100%" }}>
      {/* ── Left: list of series ───────────────────────────────────────── */}
      <div style={{ width: 320, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <h1 style={{ fontSize: 28, fontFamily: "var(--font-serif)", color: "var(--text-0)", margin: 0 }}>
          {t.seriesTitle}
        </h1>
        <p style={{ fontSize: 13, color: "var(--text-3)", margin: 0, lineHeight: 1.5 }}>
          {t.seriesIntro}
        </p>

        {/* Create new */}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t.seriesNewPlaceholder}
            className="input"
            onKeyDown={(e) => { if (e.key === "Enter") void handleCreate(); }}
          />
          <button onClick={handleCreate} disabled={!newTitle.trim() || creating} className="btn btn-primary btn-sm">
            {creating ? "…" : t.seriesNewBtn}
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          {series.length === 0 ? (
            <div className="empty" style={{ fontSize: 13 }}>{t.seriesEmpty}</div>
          ) : (
            series.map((s) => {
              const count = articlesBySeries.get(s.id)?.length || 0;
              const active = s.id === selectedId;
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className="card"
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    padding: 10,
                    cursor: "pointer",
                    textAlign: "left",
                    border: active ? "1px solid var(--accent)" : undefined,
                    background: active ? "rgba(184,134,74,0.08)" : undefined,
                  }}
                >
                  {s.cover ? (
                    <img
                      src={fileUrl("series", s.id, s.cover, "128x128")}
                      alt=""
                      style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover", flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: 4, background: "var(--bg-2)", flexShrink: 0, display: "grid", placeItems: "center", fontFamily: "var(--font-serif)", color: "var(--text-3)" }}>
                      {s.title.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-0)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.title}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                      {t.seriesCount(count)}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── Right: selected series detail ───────────────────────────────── */}
      <div style={{ flex: 1, overflow: "auto", paddingRight: 8 }}>
        {selectedSeries ? (
          <SeriesDetail
            key={selectedSeries.id}
            t={t}
            series={selectedSeries}
            articles={articlesBySeries.get(selectedSeries.id) || []}
            allArticles={articles}
            onSaveMeta={handleSaveMeta}
            onUploadCover={handleUploadCover}
            onDelete={handleDelete}
            onReorderArticle={handleReorderArticle}
            onRemoveFromSeries={handleRemoveFromSeries}
            onAttachArticle={async (articleId, position) => {
              try {
                await updateRecord<Article>("articles", articleId, {
                  series: selectedSeries.id,
                  series_position: position,
                });
                await load();
              } catch (err: any) {
                alert("Attach failed: " + (err?.message || err));
              }
            }}
          />
        ) : (
          <div className="empty" style={{ marginTop: 80 }}>
            {t.seriesSelectHint}
          </div>
        )}
      </div>
    </div>
  );
}

// ── SeriesDetail ─────────────────────────────────────────────────────────

function SeriesDetail({
  t,
  series,
  articles,
  allArticles,
  onSaveMeta,
  onUploadCover,
  onDelete,
  onReorderArticle,
  onRemoveFromSeries,
  onAttachArticle,
}: {
  t: T;
  series: Series;
  articles: Article[];
  allArticles: Article[];
  onSaveMeta: (s: Series, patch: Partial<Series>) => Promise<void>;
  onUploadCover: (s: Series, file: File) => Promise<void>;
  onDelete: (s: Series) => Promise<void>;
  onReorderArticle: (a: Article, newPos: number) => Promise<void>;
  onRemoveFromSeries: (a: Article) => Promise<void>;
  onAttachArticle: (articleId: string, position: number) => Promise<void>;
}) {
  const [title, setTitle] = useState(series.title);
  const [description, setDescription] = useState(series.description || "");

  // Re-sync if user switches series
  useEffect(() => {
    setTitle(series.title);
    setDescription(series.description || "");
  }, [series.id]);

  const unattached = allArticles.filter((a) => !a.series);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Meta */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <label className="label">{t.seriesLabelTitle}</label>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => { if (title !== series.title) void onSaveMeta(series, { title }); }}
              style={{ fontSize: 18, fontFamily: "var(--font-serif)" }}
            />
            <label className="label" style={{ marginTop: 14 }}>{t.seriesLabelDescription}</label>
            <textarea
              className="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => { if (description !== (series.description || "")) void onSaveMeta(series, { description }); }}
              rows={3}
              placeholder={t.seriesDescPlaceholder}
            />
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 6 }}>
              Slug: <code>{series.slug}</code>
            </div>
          </div>
          <CoverUpload series={series} onUpload={onUploadCover} />
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={() => void onDelete(series)} className="btn btn-danger btn-sm">
            {t.seriesDelete}
          </button>
        </div>
      </div>

      {/* Articles in series */}
      <div>
        <h2 style={{ fontSize: 18, color: "var(--text-0)", margin: "0 0 12px" }}>
          {t.seriesArticlesIn(articles.length)}
        </h2>
        {articles.length === 0 ? (
          <div className="empty" style={{ fontSize: 13 }}>
            {t.seriesNoArticlesAttached}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {articles.map((a, i) => (
              <div key={a.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                <div style={{ fontSize: 14, fontFamily: "var(--font-mono)", color: "var(--text-3)", width: 30 }}>
                  {a.series_position || i + 1}
                </div>
                <a
                  href={`#/article/${a.id}`}
                  style={{ flex: 1, color: "var(--text-0)", textDecoration: "none", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={a.title}
                >
                  {a.title}
                  <span className={`badge badge-${a.status}`} style={{ marginLeft: 8, fontSize: 10 }}>{a.status}</span>
                </a>
                <button onClick={() => void onReorderArticle(a, (a.series_position || 1) - 1)} className="btn btn-ghost btn-sm" title="Move up">↑</button>
                <button onClick={() => void onReorderArticle(a, (a.series_position || 1) + 1)} className="btn btn-ghost btn-sm" title="Move down">↓</button>
                <button onClick={() => void onRemoveFromSeries(a)} className="btn btn-ghost btn-sm" title="Remove from series">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unattached articles */}
      {unattached.length > 0 && (
        <div>
          <h2 style={{ fontSize: 14, color: "var(--text-2)", margin: "0 0 8px", fontWeight: 500 }}>
            {t.seriesAddArticles(unattached.length)}
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 280, overflow: "auto", paddingRight: 4 }}>
            {unattached.slice(0, 50).map((a) => (
              <button
                key={a.id}
                onClick={() => void onAttachArticle(a.id, articles.length + 1)}
                className="card"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", textAlign: "left" }}
                title="Add as last in series"
              >
                <span style={{ flex: 1, fontSize: 13, color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {a.title}
                </span>
                <span className={`badge badge-${a.status}`} style={{ fontSize: 10 }}>{a.status}</span>
                <span style={{ fontSize: 11, color: "var(--accent)" }}>+ Add</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── CoverUpload ──────────────────────────────────────────────────────────

function CoverUpload({
  series,
  onUpload,
}: {
  series: Series;
  onUpload: (s: Series, file: File) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File) {
    setBusy(true);
    try {
      await onUpload(series, file);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label
      style={{
        display: "block",
        width: 140,
        height: 90,
        background: "var(--bg-2)",
        border: "1px dashed var(--border-1)",
        borderRadius: 6,
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {series.cover ? (
        <img
          src={fileUrl("series", series.id, series.cover, "480x270")}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div style={{ display: "grid", placeItems: "center", height: "100%", color: "var(--text-3)", fontSize: 12, padding: 8, textAlign: "center" }}>
          {busy ? "Uploading…" : "Drop cover image"}
        </div>
      )}
      <input
        type="file"
        accept="image/*"
        style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />
    </label>
  );
}
